import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
    useGetMetricByIdQuery,
    useCreateMetricMutation,
    useEditMetricMutation,
    useGetSubscribersQuery
} from "../../../services";
import { IMetric, IStatus, AccessControl } from "../../../types";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";

const Metrics: React.FC = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { metricid } = useParams();

    const {
        data: metricResponse,
        isLoading: isMetricLoading,
        isError: isMetricError,
    } = useGetMetricByIdQuery(metricid as string, {
        skip: !metricid,
    });

    const [createMetric, { isLoading: isCreating }] = useCreateMetricMutation();
    const [editMetric, { isLoading: isUpdating }] = useEditMetricMutation();
    const { data: subscribersResponse } = useGetSubscribersQuery();

    const isSaving = isCreating || isUpdating;

    const [formData, setFormData] = useState<Partial<IMetric>>({
        name: "",
        description: "",
        access: AccessControl.GLOBAL,
        subscriber: "",
        status: "Active" as IStatus,
    });

    const [errors, setErrors] = useState<Partial<Record<keyof IMetric, string>>>({});

    // Hydrate form when metric detail loads
    useEffect(() => {
        if (!metricResponse) return;
        const m = metricResponse;
        setFormData({
            name: m.name ?? "",
            description: m.description ?? "",
            access: m.access ?? AccessControl.GLOBAL,
            subscriber: typeof m.subscriber === "object" ? (m.subscriber as any)?._id : m.subscriber ?? "",
            status: m.status ?? "Active",
        });
    }, [metricResponse]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name as keyof IMetric])
            setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof IMetric, string>> = {};
        if (!formData.name?.trim()) newErrors.name = "Name is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (metricid) {
                await editMetric({
                    id: metricid,
                    data: formData,
                }).unwrap();
            } else {
                await createMetric(formData).unwrap();
            }
            navigate(adminRoutes.metricsList);
        } catch (err) {
            console.error("Save metric failed:", err);
        }
    };

    // Sidebar compensation
    const isSidebarOpen = useAppSelector(
        (state: RootState) => state.navbar.isOpen
    );
    const sidebarWidth = isSidebarOpen ? 256 : 64;

    if (metricid && isMetricLoading) {
        return (
            <div className={`pt-20 text-center ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Loading metric...
            </div>
        );
    }

    return (
        <div
            className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-black" : "bg-gray-50"
                }`}
            style={{
                marginLeft: sidebarWidth,
                width: `calc(100% - ${sidebarWidth}px)`,
            }}
        >
            {/* Header */}
            <div className="mb-6">
                <h1
                    className={`text-2xl font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-800"
                        }`}
                >
                    {metricid ? "Edit Metric" : "Create Metric"}
                </h1>
                <div className="flex items-center gap-2 mt-2 text-sm">
                    <Link
                        to={adminRoutes.metricsList}
                        className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
                    >
                        All Metrics
                    </Link>
                    <span className={theme === "dark" ? "text-gray-400" : "text-gray-600"}>/</span>
                    <span className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}>
                        {metricid ? "Edit Metric" : "Create Metric"}
                    </span>
                </div>
            </div>

            <div
                className={`${theme === "dark"
                    ? "bg-[#1E1E1E] border border-gray-800"
                    : "bg-white border border-gray-200"
                    } rounded-lg p-6`}
            >
                <div className="mb-6">
                    <h2
                        className={`text-base font-medium border-b pb-2 border-gray-700 ${theme === "dark" ? "text-white" : "text-gray-800"
                            }`}
                    >
                        Metric Information
                    </h2>
                </div>

                {isMetricError && (
                    <p className="text-red-500 mb-4">Failed to load metric details.</p>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Name */}
                        <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Metric Name
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter metric name"
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.name
                                    ? "border border-red-500"
                                    : theme === "dark"
                                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                        </div>

                        {/* Access */}
                        {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Access
                            </label>
                            <select
                                name="access"
                                value={formData.access}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white"
                                    : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value={AccessControl.GLOBAL}>Global</option>
                                <option value={AccessControl.SUBSCRIBER}>Subscriber</option>
                                <option value={AccessControl.ORGANISATION}>Organisation</option>
                            </select>
                        </div> */}

                        {/* Subscriber */}
                        {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Subscriber
                            </label>
                            <select
                                name="subscriber"
                                value={formData.subscriber as string}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white"
                                    : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="" disabled>Select Subscriber</option>
                                {subscribersResponse?.map((sub: any) => (
                                    <option key={sub._id} value={sub._id}>
                                        {sub.name}
                                    </option>
                                ))}
                            </select>
                        </div> */}

                        {/* Status */}
                        {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.status
                                    ? "border border-red-500"
                                    : theme === "dark"
                                        ? "bg-black border border-gray-800 text-white"
                                        : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                            {errors.status && (
                                <p className="text-red-500 text-xs mt-1">{errors.status}</p>
                            )}
                        </div> */}

                        {/* Description */}
                        <div className="md:col-span-2">
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter metric description"
                                rows={4}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-8 py-2 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                            style={{
                                background:
                                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                            }}
                        >
                            {isSaving ? "Saving..." : metricid ? "Update" : "Create"}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(adminRoutes.metricsList)}
                            className={`px-8 py-2 rounded-md font-medium ${theme === "dark"
                                ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                                }`}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Metrics;
